import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, Download, Trash2, RefreshCw, FileText, Package,
  AlertCircle, CheckCircle2, Settings, Database, AlertTriangle, RotateCcw,
  ShieldCheck, ShieldAlert, X, ChevronDown, ChevronUp, Loader2, Plus, Layers,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import AutoDiscoverArcGISDialog from '../../components/parcel/AutoDiscoverArcGISDialog';
import { safeJsonFetch, authHeaders, authHeadersForFormData, formatApiError } from '../../lib/safeFetch';

const API = process.env.REACT_APP_BACKEND_URL;
const ACCEPT_EXTS = '.csv,.geojson,.json,.kml,.xlsx,.zip';
const SMALL_THRESHOLD_MB = 25; // anything bigger than this goes via chunked path

// Parcel Converter — splits a parcel file into many small CSVs ready for V2 upload.
// Designed to be the path of least resistance when reverse-proxy body limits
// make the chunked upload painful for 200+ MB shapefiles.
const AdminParcelConverter = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [rowsPerCsv, setRowsPerCsv] = useState(25000);
  const [working, setWorking] = useState(false);
  const [progress, setProgress] = useState(null);
  const [message, setMessage] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  // Per-job collapsed state. Jobs default to COLLAPSED so the Conversion
  // Jobs list stays scannable even with 8+ historical jobs holding 20+
  // parts each (the page used to grow to ~10K pixels tall). A job is
  // auto-expanded when:
  //   1. It's the user's currentJobId (just uploaded / started a fetch)
  //   2. It's actively running (uploading / converting / finalizing)
  //   3. It has a stuck-recovery banner the admin needs to see
  // We track an explicit *toggled* set so manual clicks override the
  // auto-rule: once you collapse a running job, it stays collapsed even
  // when its status updates on the next poll.
  // Per-county audit-report state, populated from /parcel-v2/audit-reports.
  // The list grows over time as admins click "Audit" on Parcel Data Mgmt
  // county pages — Parcel Converter is the operational hub where they
  // act on the findings via Wipe & Re-fetch.
  const [auditReports, setAuditReports] = useState([]);
  const [loadingAudits, setLoadingAudits] = useState(true);
  const [rebuildingCounty, setRebuildingCounty] = useState(null); // county_id mid-rebuild

  const [collapsedToggles, setCollapsedToggles] = useState({});
  const toggleJobCollapse = (jobId, currentlyExpanded) => {
    setCollapsedToggles((prev) => ({ ...prev, [jobId]: !currentlyExpanded }));
  };
  const isJobExpanded = (job) => {
    const override = collapsedToggles[job.job_id];
    if (override === true) return true;
    if (override === false) return false;
    // Defaults when no manual override:
    if (job.job_id === currentJobId) return true;
    if (job.is_stuck) return true;
    if (job.push_status === 'running') return true;
    const running = ['uploading', 'converting', 'finalizing'].includes(job.status);
    if (running) return true;
    return false;
  };

  const token = (typeof window !== 'undefined' && (localStorage.getItem('token') || localStorage.getItem('admin_token'))) || '';

  // ---- Parcel Fetcher: auto-download a county's parcels from ArcGIS ----
  // Loaded once on mount. `auto_fetch=false` rows are rendered but disabled.
  const [fetcherCounties, setFetcherCounties] = useState([]);
  const [fetcherCountyId, setFetcherCountyId] = useState('');
  const [fetcherResidentialOnly, setFetcherResidentialOnly] = useState(true);
  const [fetcherStarting, setFetcherStarting] = useState(false);

  // ---- Endpoint validator: pings every configured county in parallel
  // to surface dead URLs (HTTP 404 / DNS errors / removed services) and
  // large drift between reported vs estimated parcel counts. Mirrors the
  // Dallas + Denver bugs that prompted this feature.
  const [validateModalOpen, setValidateModalOpen] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validateReport, setValidateReport] = useState(null);
  const handleValidateEndpoints = async () => {
    setValidateModalOpen(true);
    setValidating(true);
    setValidateReport(null);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/fetcher/validate`, {
      headers: authHeaders(),
    });
    setValidating(false);
    if (!res.ok) {
      setValidateReport({ error: formatApiError(res) });
      return;
    }
    setValidateReport(res.data);
  };
  const loadFetcherCounties = useCallback(async () => {
    const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/fetcher/counties`, { headers: authHeaders() });
    if (res.ok && Array.isArray(res.data?.counties)) {
      setFetcherCounties(res.data.counties);
    }
  }, []);

  // ---- Add New County: register a county missing from the dropdown,
  // then run ArcGIS auto-discovery so it becomes fetchable in one flow.
  const [addCountyOpen, setAddCountyOpen] = useState(false);
  const [addCountyForm, setAddCountyForm] = useState({ name: '', state: '', city: '' });
  const [addCountySaving, setAddCountySaving] = useState(false);
  const [discoverFor, setDiscoverFor] = useState(null); // { county_id, name }
  const discoverPromotedRef = useRef(false);
  const handleAddCounty = async () => {
    const name = addCountyForm.name.trim();
    const state = addCountyForm.state.trim().toUpperCase();
    if (!name || state.length !== 2) {
      setMessage({ type: 'error', text: 'Enter a county name and a 2-letter state code (e.g. St. Charles + MO).' });
      return;
    }
    setAddCountySaving(true);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/register-county`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ county_name: name, state, city: addCountyForm.city.trim() || null }),
    });
    setAddCountySaving(false);
    if (!res.ok) {
      setMessage({ type: 'error', text: `Failed to add county: ${formatApiError(res)}` });
      return;
    }
    const d = res.data || {};
    await loadFetcherCounties();
    setFetcherCountyId(d.county_id || '');
    setAddCountyOpen(false);
    setAddCountyForm({ name: '', state: '', city: '' });
    if (d.already_exists && !d.manual_only) {
      setMessage({ type: 'info', text: `${d.name} is already in the list and fetchable — selected it for you.` });
      return;
    }
    setMessage({
      type: 'info',
      text: d.already_exists
        ? `${d.name} is already listed (manual only) — searching ArcGIS for its parcel endpoint…`
        : `${d.name} added — searching ArcGIS for its parcel endpoint…`,
    });
    setDiscoverFor({ county_id: d.county_id, name: d.name });
  };

  // ---- Bulk County Discovery: queue many counties at once; the backend
  // registers each, searches ArcGIS for its parcel source and auto-wires
  // the best candidate. Runs in the background — safe to close the tab.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkJob, setBulkJob] = useState(null);
  const [bulkBannerDismissed, setBulkBannerDismissed] = useState(false);

  const parseBulkLines = (text) => {
    const items = [];
    const errors = [];
    text.split('\n').map((l) => l.trim()).filter(Boolean).forEach((line) => {
      const m = line.match(/^(.+?)[,\s]+([A-Za-z]{2})$/);
      if (!m) { errors.push(line); return; }
      items.push({ county_name: m[1].replace(/,+$/, '').trim(), state: m[2].toUpperCase() });
    });
    return { items, errors };
  };

  const handleBulkSubmit = async () => {
    const { items, errors } = parseBulkLines(bulkText);
    if (errors.length) {
      setMessage({ type: 'error', text: `Could not parse: "${errors.slice(0, 3).join('" · "')}" — use one "County, ST" per line.` });
      return;
    }
    if (!items.length) return;
    setBulkSubmitting(true);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/bulk-discover`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ counties: items }),
    });
    setBulkSubmitting(false);
    if (!res.ok) {
      setMessage({ type: 'error', text: `Bulk discovery failed to start: ${formatApiError(res)}` });
      return;
    }
    setBulkText('');
    setBulkBannerDismissed(false);
    const jRes = await safeJsonFetch(`${API}/api/admin/parcel-converter/bulk-discover/${res.data.job_id}`, { headers: authHeaders() });
    if (jRes.ok && jRes.data?.job) setBulkJob(jRes.data.job);
  };

  // Resume showing a bulk run after a page reload (overnight use case).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/bulk-discover/latest`, { headers: authHeaders() });
      if (!cancelled && res.ok && res.data?.job) setBulkJob(res.data.job);
    })();
    return () => { cancelled = true; };
  }, []);

  // Poll a running bulk job every 3s; refresh the county dropdown when done.
  useEffect(() => {
    if (!bulkJob || bulkJob.status !== 'running') return undefined;
    const iv = setInterval(async () => {
      const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/bulk-discover/${bulkJob.job_id}`, { headers: authHeaders() });
      if (res.ok && res.data?.job) {
        setBulkJob(res.data.job);
        if (res.data.job.status !== 'running') loadFetcherCounties();
      }
    }, 3000);
    return () => clearInterval(iv);
  }, [bulkJob?.job_id, bulkJob?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const bulkDoneCount = (bulkJob?.items || []).filter((it) => !['queued', 'discovering'].includes(it.status)).length;
  const bulkRecent = bulkJob && (
    bulkJob.status === 'running'
    || (bulkJob.finished_at && (Date.now() - new Date(bulkJob.finished_at).getTime()) < 24 * 3600 * 1000)
  );
  const handleStartFetch = async () => {
    if (!fetcherCountyId) return;
    const county = fetcherCounties.find((c) => c.county_id === fetcherCountyId);
    if (county && !county.auto_fetch) {
      setMessage({ type: 'error', text: `${county.name} requires manual download. Use the Upload & Split section below.` });
      return;
    }
    if (!window.confirm(
      `Start fetching ALL parcels for ${county?.name}?\n\nEstimated ${county?.estimated_parcels?.toLocaleString() || '—'} parcels. Large counties can take 30–120 minutes.\n\nResidential only: ${fetcherResidentialOnly ? 'YES' : 'no — includes commercial/industrial'}.`
    )) return;
    setFetcherStarting(true);
    const form = new FormData();
    form.append('county_id', fetcherCountyId);
    form.append('residential_only', fetcherResidentialOnly ? 'true' : 'false');
    form.append('rows_per_csv', String(rowsPerCsv));
    const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/fetcher/start`, {
      method: 'POST',
      headers: authHeadersForFormData(),  // critical: no Content-Type — browser adds multipart boundary
      body: form,
    });
    setFetcherStarting(false);
    if (!res.ok) {
      setMessage({ type: 'error', text: `Fetch failed to start: ${formatApiError(res)}` });
      return;
    }
    setMessage({ type: 'info', text: `Fetch started for ${county?.name}. Watch the job below; parts finalize as rows pour in.` });
    setCurrentJobId(res.data?.job_id);
    loadJobs();
  };

  // ---- Push to Parcel Data Mgmt ----
  const [pushModal, setPushModal] = useState(null); // { jobId, filename }
  const [v2Counties, setV2Counties] = useState([]);
  const [selectedCountyId, setSelectedCountyId] = useState('');
  const [pushing, setPushing] = useState(false);

  const openPushModal = async (job) => {
    setPushModal({ jobId: job.job_id, filename: job.filename });
    setSelectedCountyId('');
    if (v2Counties.length === 0) {
      const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/v2-counties`, { headers: authHeaders() });
      if (res.ok && res.data?.counties) setV2Counties(res.data.counties);
    }
  };
  const closePushModal = () => { setPushModal(null); setSelectedCountyId(''); };

  const handleStartPush = async () => {
    if (!selectedCountyId || !pushModal) return;
    setPushing(true);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/jobs/${pushModal.jobId}/push-to-county`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ county_id: selectedCountyId }),
    });
    setPushing(false);
    if (!res.ok) {
      setMessage({ type: 'error', text: `Push failed to start: ${res.error}` });
      return;
    }
    setMessage({ type: 'success', text: res.data?.message || 'Push started.' });
    closePushModal();
    // Poll the job until push completes
    let polls = 0;
    const tick = async () => {
      polls++;
      const sRes = await safeJsonFetch(`${API}/api/admin/parcel-converter/jobs/${pushModal.jobId}`, { headers: authHeaders() });
      if (sRes.ok && sRes.data) {
        // Update the job inline in the list so UI shows live progress
        setJobs((prev) => prev.map((j) => (j.job_id === pushModal.jobId ? sRes.data : j)));
        if (sRes.data.push_status === 'completed' || sRes.data.push_status === 'failed') {
          setMessage({
            type: sRes.data.push_status === 'completed' ? 'success' : 'error',
            text: sRes.data.push_message || 'Push finished',
          });
          return;
        }
      }
      if (polls < 1200) setTimeout(tick, 1500);
    };
    setTimeout(tick, 1500);
  };

  // ---- jobs list ----
  const loadJobs = useCallback(async (opts) => {
    const silent = !!(opts && opts.silent);
    if (!silent) setLoadingJobs(true);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/jobs`, { headers: authHeaders() });
    if (res.ok && res.data?.jobs) setJobs(res.data.jobs);
    if (!silent) setLoadingJobs(false);
  }, []);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Live push progress: while ANY job is pushing, silently refresh the
  // jobs list every 3s so the progress bar + parcel counts move even
  // after a page reload (the push itself runs server-side).
  const anyPushRunning = jobs.some((j) => j.push_status === 'running');
  useEffect(() => {
    if (!anyPushRunning) return undefined;
    const iv = setInterval(() => loadJobs({ silent: true }), 3000);
    return () => clearInterval(iv);
  }, [anyPushRunning, loadJobs]);

  // Audit reports loader — runs on mount + after a Wipe & Re-fetch
  // kicks off (so the stale "has dupes" badge is replaced with a fresh
  // "rebuilding" indicator). Light call: a single Mongo find on
  // `parcel_audit_reports` capped at 200 rows.
  const loadAuditReports = useCallback(async () => {
    setLoadingAudits(true);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-v2/audit-reports`, {
      headers: authHeaders(),
    });
    setLoadingAudits(false);
    if (res.ok && res.data?.reports) {
      setAuditReports(res.data.reports);
    }
  }, []);
  useEffect(() => { loadAuditReports(); }, [loadAuditReports]);

  // One-click "Wipe & Re-fetch from ArcGIS" for a county with detected
  // duplicates. Confirms first, then POSTs to the orchestration endpoint
  // which (1) wipes the V2 collection, (2) starts a fresh ArcGIS fetcher
  // job, (3) auto-pushes the converted CSVs back into V2 when the fetch
  // completes. Returns immediately with the new job_id so we can scroll
  // the Conversion Jobs panel into view.
  const rebuildCounty = async (countyId, countyName) => {
    if (!window.confirm(
      `Delete every parcel in ${countyName} and re-fetch fresh data from ArcGIS?\n\n` +
      `The new fetch runs in the background — usually 10–60 min for a county this size. ` +
      `The audit row will refresh once the new data lands.`
    )) return;
    setRebuildingCounty(countyId);
    const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/county/${countyId}/rebuild`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    });
    setRebuildingCounty(null);
    if (res.ok && res.data?.job_id) {
      setMessage({
        type: 'success',
        text:
          `Wiped ${(res.data.wiped_parcels || 0).toLocaleString()} old parcels for ${countyName}. ` +
          `New ArcGIS fetch started (job ${res.data.job_id.slice(0, 8)}…) — watch progress below.`,
      });
      setCurrentJobId(res.data.job_id);
      loadJobs();
    } else {
      setMessage({
        type: 'error',
        text: `Rebuild failed: ${res.data?.detail || res.error || `HTTP ${res.status}`}`,
      });
    }
  };
  useEffect(() => { loadFetcherCounties(); }, [loadFetcherCounties]);

  // ---- pollers ----
  const pollJob = (jobId) => {
    let polls = 0;
    const tick = async () => {
      polls++;
      const sRes = await safeJsonFetch(`${API}/api/admin/parcel-converter/jobs/${jobId}`, { headers: authHeaders() });
      if (sRes.ok && sRes.data) {
        setProgress(sRes.data);
        // Live-update the jobs list while a job is in progress so the user
        // sees the in-flight job (and its growing list of finalized parts)
        // in the Conversion Jobs panel below — not just after completion.
        setJobs((prev) => {
          const next = [...prev];
          const i = next.findIndex((j) => j.job_id === jobId);
          if (i >= 0) next[i] = sRes.data;
          else next.unshift(sRes.data);
          return next;
        });
        if (sRes.data.status === 'completed' || sRes.data.status === 'failed') {
          setWorking(false);
          setMessage({
            type: sRes.data.status === 'completed' ? 'success' : 'error',
            text: sRes.data.message || (sRes.data.status === 'completed' ? 'Conversion complete' : 'Conversion failed'),
          });
          loadJobs();
          return;
        }
      }
      if (polls < 1200) setTimeout(tick, 1500);
    };
    setTimeout(tick, 1000);
  };

  // ---- upload (small) ----
  const handleSmallUpload = async (file, ext) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('rows_per_csv', String(rowsPerCsv));
    setWorking(true);
    setProgress({ phase: 'uploading', message: `Uploading ${file.name}...`, upload_progress: 0 });
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/api/admin/parcel-converter/upload`, true);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        setProgress({ phase: 'uploading', message: `Uploading ${file.name}...`, upload_progress: Math.round((ev.loaded / ev.total) * 100) });
      }
    };
    xhr.onload = () => {
      let data = null;
      try { data = JSON.parse(xhr.responseText); } catch { /* noop */ }
      if (xhr.status >= 200 && xhr.status < 300 && data?.job_id) {
        setCurrentJobId(data.job_id);
        setProgress({ phase: 'converting', message: 'Conversion started...', upload_progress: 100 });
        pollJob(data.job_id);
      } else {
        setWorking(false);
        setProgress(null);
        setMessage({ type: 'error', text: `Upload failed (HTTP ${xhr.status}): ${data?.detail || 'unknown error'}` });
      }
    };
    xhr.onerror = () => {
      setWorking(false);
      setProgress(null);
      setMessage({ type: 'error', text: 'Network error during upload' });
    };
    xhr.send(formData);
  };

  // ---- upload (chunked, for large files) ----
  const handleChunkedUpload = async (file, ext) => {
    const chunkSize = 4 * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkSize);
    setWorking(true);
    setProgress({ phase: 'init', message: 'Opening upload session...', upload_progress: 0 });
    const initRes = await safeJsonFetch(`${API}/api/admin/parcel-converter/chunked/init`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename: file.name,
        file_format: ext,
        total_bytes: file.size,
        total_chunks: totalChunks,
        rows_per_csv: rowsPerCsv,
      }),
    });
    if (!initRes.ok || !initRes.data?.job_id) {
      setWorking(false);
      setProgress(null);
      setMessage({ type: 'error', text: `Init failed (HTTP ${initRes.status || 0}): ${initRes.error || 'unknown'}` });
      return;
    }
    const jobId = initRes.data.job_id;
    setCurrentJobId(jobId);

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const blob = file.slice(start, end);
      const fd = new FormData();
      fd.append('chunk', blob, `chunk_${i}`);
      fd.append('chunk_index', String(i));
      const ok = await new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API}/api/admin/parcel-converter/chunked/append/${jobId}`, true);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.onload = () => resolve(xhr.status >= 200 && xhr.status < 300);
        xhr.onerror = () => resolve(false);
        xhr.send(fd);
      });
      if (!ok) {
        setWorking(false);
        setProgress(null);
        setMessage({ type: 'error', text: `Chunk ${i + 1}/${totalChunks} failed — please try again.` });
        loadJobs();
        return;
      }
      const pct = Math.round(((i + 1) / totalChunks) * 100);
      setProgress({ phase: 'uploading', message: `Uploading chunk ${i + 1} of ${totalChunks} (${pct}%)...`, upload_progress: pct });
    }
    setProgress({ phase: 'finalizing', message: 'Upload complete. Starting conversion...', upload_progress: 100 });
    const finRes = await safeJsonFetch(`${API}/api/admin/parcel-converter/chunked/finalize/${jobId}`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!finRes.ok) {
      setWorking(false);
      setProgress(null);
      setMessage({ type: 'error', text: `Finalize failed: ${finRes.error}` });
      return;
    }
    pollJob(jobId);
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['csv', 'geojson', 'json', 'kml', 'xlsx', 'zip'].includes(ext)) {
      setMessage({ type: 'error', text: `Unsupported file type: .${ext}` });
      e.target.value = '';
      return;
    }
    if (rowsPerCsv < 100 || rowsPerCsv > 1_000_000) {
      setMessage({ type: 'error', text: 'rows_per_csv must be between 100 and 1,000,000' });
      e.target.value = '';
      return;
    }
    setMessage(null);
    setProgress(null);
    const isLarge = file.size > SMALL_THRESHOLD_MB * 1024 * 1024;
    if (isLarge) {
      await handleChunkedUpload(file, ext);
    } else {
      await handleSmallUpload(file, ext);
    }
    e.target.value = '';
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm(`Permanently delete job ${jobId} and its output files?`)) return;
    await safeJsonFetch(`${API}/api/admin/parcel-converter/jobs/${jobId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (currentJobId === jobId) {
      setCurrentJobId(null);
      setProgress(null);
    }
    // Drop the collapse-toggle entry so the in-memory map doesn't grow
    // unbounded for the lifetime of the page.
    setCollapsedToggles((prev) => {
      if (!(jobId in prev)) return prev;
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
    loadJobs();
  };

  // Stuck-job recovery: flips status to failed (the backend daemon thread,
  // if any, is already dead — see services/parser_thread.py). Optionally
  // kicks off retry_conversion which re-runs the parser on the existing
  // source.bin without re-uploading.
  const handleStuckRecover = async (jobId, andRetry = true) => {
    const verb = andRetry ? 'mark as failed AND restart conversion' : 'mark as failed';
    if (!window.confirm(`This job appears stuck (no heartbeat in 2+ minutes). ${verb}?\n\n${andRetry ? 'The original source file is reused — no re-upload needed.' : 'You can manually retry or delete it afterward.'}`)) return;
    setMessage({ type: 'info', text: `Recovering job ${jobId}...` });
    const failRes = await safeJsonFetch(`${API}/api/admin/parcel-converter/jobs/${jobId}/mark-failed`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!failRes.ok) {
      setMessage({ type: 'error', text: `Cleanup failed: ${failRes.data?.detail || failRes.error || 'unknown'}` });
      return;
    }
    if (andRetry) {
      const retryRes = await safeJsonFetch(`${API}/api/admin/parcel-converter/jobs/${jobId}/retry`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!retryRes.ok) {
        setMessage({ type: 'error', text: `Retry failed: ${retryRes.data?.detail || retryRes.error || 'unknown'} — you can still delete the job manually.` });
        loadJobs();
        return;
      }
      setMessage({ type: 'info', text: `Job ${jobId} restarted. Watch the progress bar — heartbeat updates fire every ~2s now.` });
    } else {
      setMessage({ type: 'info', text: `Job ${jobId} marked as failed. You can delete it via the trash icon.` });
    }
    loadJobs();
  };

  // Resume a failed Parcel Fetcher job from its last saved checkpoint.
  // Continues paginating ArcGIS from the exact offset that failed and
  // appends to the partial CSV — no data is re-downloaded.
  const handleResumeFetch = async (job) => {
    const offset = Number(job.resume_offset || 0);
    const emitted = Number(job.rows_emitted || 0);
    const parts = (job.output_files || []).length;
    const msg = (
      `Resume fetching from ${job.county_name || job.county_id}?\n\n` +
      `Already saved: ${emitted.toLocaleString()} rows across ${parts} part(s)\n` +
      `Will continue from ArcGIS offset: ${offset.toLocaleString()}\n\n` +
      `No data is re-downloaded — only the missing records are fetched.`
    );
    if (!window.confirm(msg)) return;
    setMessage({ type: 'info', text: `Resuming fetch ${job.job_id} from offset ${offset.toLocaleString()}...` });
    const res = await safeJsonFetch(`${API}/api/admin/parcel-converter/jobs/${job.job_id}/resume-fetch`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!res.ok) {
      setMessage({ type: 'error', text: `Resume failed: ${res.data?.detail || res.error || 'unknown'}` });
      return;
    }
    setMessage({
      type: 'success',
      text: res.data?.message || `Resume started for ${job.job_id}.`,
    });
    loadJobs();
  };

  // ---- helpers ----
  const fileUrl = (jobId, filename) =>
    `${API}/api/admin/parcel-converter/jobs/${jobId}/files/${encodeURIComponent(filename)}?token=${encodeURIComponent(token)}`;
  const zipUrl = (jobId) =>
    `${API}/api/admin/parcel-converter/jobs/${jobId}/zip?token=${encodeURIComponent(token)}`;
  const fmtBytes = (b) => {
    if (b == null) return '—';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  // Render

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="admin-parcel-converter-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900" data-testid="parcel-converter-title">Parcel Converter</h1>
        <p className="text-sm text-gray-500 mt-1">
          Split a large parcel file (Shapefile, GeoJSON, KML, Excel, CSV) into many smaller CSVs ready for upload to Parcel Data Mgmt. Source coordinates are preserved verbatim — no reprojection.
        </p>
      </div>

      {/* Parcel Fetcher — auto-download a county's parcels from its
           public ArcGIS REST API directly into the converter pipeline.
           Output appears as a normal converter job below so the existing
           "Push to Parcel Data Mgmt" button works without any extra step. */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-5" data-testid="parcel-fetcher-panel">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Database className="w-5 h-5 text-emerald-600" /> Parcel Fetcher
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              Auto-download a county's parcels directly from its public ArcGIS API — no manual file upload needed. Output streams into the converter as CSV parts and can be pushed to Parcel Data Mgmt with one click below.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddCountyOpen(true)}
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-100"
              data-testid="add-new-county-btn"
              title="Add a county that isn't in the dropdown, then auto-discover its ArcGIS parcel endpoint"
            >
              <Plus className="w-4 h-4 mr-1" /> Add New County
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBulkOpen(true)}
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-100"
              data-testid="bulk-add-counties-btn"
              title="Queue several counties at once — each is added and its ArcGIS parcel source auto-discovered in the background"
            >
              <Layers className="w-4 h-4 mr-1" /> Bulk Add Counties
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleValidateEndpoints}
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-100"
              data-testid="validate-endpoints-btn"
              title="Ping every configured ArcGIS endpoint and surface dead URLs or large count drift before starting a fetch"
            >
              <ShieldCheck className="w-4 h-4 mr-1" /> Validate all endpoints
            </Button>
          </div>
        </div>
        {bulkRecent && !bulkBannerDismissed && !bulkOpen && (
          <div className="mb-3 px-3 py-2 rounded-lg border border-emerald-300 bg-white/80 flex items-center justify-between gap-2 flex-wrap" data-testid="bulk-discovery-banner">
            <p className="text-xs text-gray-700 flex items-center gap-1.5">
              {bulkJob.status === 'running' ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" />
                  Bulk discovery running — {bulkDoneCount}/{(bulkJob.items || []).length} counties processed…</>
              ) : (
                <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  {bulkJob.message || `Bulk discovery ${bulkJob.status}`}</>
              )}
            </p>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} className="h-7 text-xs border-emerald-300 text-emerald-800" data-testid="bulk-banner-view-btn">
                View results
              </Button>
              <button onClick={() => setBulkBannerDismissed(true)} className="text-gray-400 hover:text-gray-600" data-testid="bulk-banner-dismiss-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">County</label>
            <select
              value={fetcherCountyId}
              onChange={(e) => setFetcherCountyId(e.target.value)}
              className="w-full px-3 py-2 border border-emerald-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
              data-testid="fetcher-county-select"
            >
              <option value="">— pick a county —</option>
              {(() => {
                const groups = {};
                fetcherCounties.forEach((c) => {
                  if (!groups[c.state]) groups[c.state] = [];
                  groups[c.state].push(c);
                });
                return Object.keys(groups).sort().map((state) => (
                  <optgroup key={state} label={state}>
                    {groups[state].map((c) => (
                      <option key={c.county_id} value={c.county_id} disabled={!c.auto_fetch}>
                        {c.name}
                        {c.estimated_parcels ? ` · ~${c.estimated_parcels.toLocaleString()} parcels` : ''}
                        {!c.auto_fetch ? ' — manual download only' : ''}
                      </option>
                    ))}
                  </optgroup>
                ));
              })()}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Rows per output CSV</label>
            <input
              type="number"
              value={rowsPerCsv}
              onChange={(e) => setRowsPerCsv(parseInt(e.target.value) || 25000)}
              min={1000}
              step={5000}
              className="w-full px-3 py-2 border border-emerald-300 rounded-md text-sm bg-white"
              data-testid="fetcher-rows-per-csv"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid="fetcher-residential-toggle">
              <input
                type="checkbox"
                checked={fetcherResidentialOnly}
                onChange={(e) => setFetcherResidentialOnly(e.target.checked)}
                className="w-4 h-4 text-emerald-600"
              />
              <span>Residential only <span className="text-gray-500 text-xs">(skip commercial/industrial)</span></span>
            </label>
          </div>
        </div>
        {fetcherCountyId && (() => {
          const c = fetcherCounties.find((x) => x.county_id === fetcherCountyId);
          if (!c) return null;
          return (
            <div className={`mb-3 p-2.5 rounded text-xs ${c.auto_fetch ? 'bg-white border border-emerald-200 text-gray-700' : 'bg-amber-50 border border-amber-300 text-amber-900'}`}>
              {c.auto_fetch ? (
                <>
                  <span className="font-semibold">{c.name}</span> · ArcGIS endpoint:{' '}
                  <code className="bg-gray-100 px-1 rounded text-[10px]">{c.api_url}</code>
                </>
              ) : (
                <>
                  ⚠ <span className="font-semibold">{c.name}</span> requires manual download from{' '}
                  {c.source_url ? <a href={c.source_url} target="_blank" rel="noreferrer" className="underline">the county website</a> : 'the county website'}.
                  Download the shapefile zip, then use <span className="font-semibold">Upload & Split</span> below.
                </>
              )}
            </div>
          );
        })()}
        <Button
          onClick={handleStartFetch}
          disabled={!fetcherCountyId || fetcherStarting || !(fetcherCounties.find((c) => c.county_id === fetcherCountyId)?.auto_fetch)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="fetcher-start-btn"
        >
          {fetcherStarting ? (
            <>
              <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Starting...
            </>
          ) : (
            <>
              <Database className="w-4 h-4 mr-1" /> Fetch parcels for this county
            </>
          )}
        </Button>
        <p className="mt-2 text-[11px] text-gray-500">
          Large counties (1M+ parcels) can take 30–120 minutes to fully fetch. The job appears in the list below and finalizes parts continuously — you can start pushing finished parts to Parcel Data Mgmt before the full fetch completes.
        </p>

        {/* Bulk Add Counties modal */}
        {bulkOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="bulk-add-modal">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-emerald-600" /> Bulk Add Counties
                </h3>
                <button onClick={() => setBulkOpen(false)} className="text-gray-400 hover:text-gray-600" data-testid="bulk-add-close-btn">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                One county per line as <span className="font-semibold">County, ST</span>. Each is registered and its
                public ArcGIS parcel source auto-discovered + wired in the background — leave it running overnight and
                check the results here in the morning.
              </p>
              {(!bulkJob || bulkJob.status !== 'running') && (
                <>
                  <textarea
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    rows={5}
                    placeholder={'Williamson, TX\nBexar, TX\nDavidson, TN'}
                    className="w-full px-3 py-2 border border-emerald-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    data-testid="bulk-add-textarea"
                  />
                  <div className="flex justify-end gap-2 mt-2 mb-3">
                    <Button variant="outline" size="sm" onClick={() => setBulkOpen(false)} data-testid="bulk-add-cancel-btn">Close</Button>
                    <Button
                      size="sm"
                      onClick={handleBulkSubmit}
                      disabled={bulkSubmitting || !bulkText.trim()}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="bulk-add-submit-btn"
                    >
                      {bulkSubmitting
                        ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Starting…</>)
                        : (<><Layers className="w-4 h-4 mr-1" /> Start discovery ({parseBulkLines(bulkText).items.length})</>)}
                    </Button>
                  </div>
                </>
              )}
              {bulkJob && (
                <div data-testid="bulk-results-panel">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-gray-700">
                      {bulkJob.status === 'running'
                        ? `Discovering… ${bulkDoneCount}/${(bulkJob.items || []).length} done`
                        : (bulkJob.message || `Run ${bulkJob.status}`)}
                    </span>
                    {bulkJob.status === 'running' && <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />}
                  </div>
                  <div className="space-y-1.5">
                    {(bulkJob.items || []).map((it) => (
                      <div key={it.county_id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded border border-gray-200 bg-gray-50" data-testid={`bulk-item-${it.county_id}`}>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-800">{it.county_clean} County, {it.state}</p>
                          <p className="text-[11px] text-gray-500 truncate" title={it.message}>{it.message}</p>
                        </div>
                        {it.status === 'queued' && <Badge className="bg-gray-200 text-gray-700 border-0 text-[10px] shrink-0">Queued</Badge>}
                        {it.status === 'discovering' && (
                          <Badge className="bg-blue-100 text-blue-800 border-0 text-[10px] shrink-0">
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Searching
                          </Badge>
                        )}
                        {it.status === 'ready' && (
                          <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px] shrink-0">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Source found{it.state_verified ? ' ✓' : ''}
                          </Badge>
                        )}
                        {it.status === 'already_configured' && <Badge className="bg-slate-200 text-slate-700 border-0 text-[10px] shrink-0">Already wired</Badge>}
                        {it.status === 'no_match' && <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px] shrink-0">Manual only</Badge>}
                        {it.status === 'error' && <Badge className="bg-rose-100 text-rose-800 border-0 text-[10px] shrink-0">Failed</Badge>}
                      </div>
                    ))}
                  </div>
                  {bulkJob.status !== 'running' && (
                    <p className="text-[11px] text-gray-500 mt-2">
                      Counties marked <span className="font-semibold text-emerald-700">Source found</span> are ready in the
                      Parcel Fetcher dropdown above — pick one and start fetching.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add New County modal */}
        {addCountyOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="add-county-modal">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-emerald-600" /> Add New County
                </h3>
                <button onClick={() => setAddCountyOpen(false)} className="text-gray-400 hover:text-gray-600" data-testid="add-county-close-btn">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Parcel data is published per <span className="font-semibold">county</span>. If you're targeting a city
                (e.g. St. Peters, MO), enter the county that covers it — St. Charles — and the state.
                After adding, we'll search ArcGIS automatically for its public parcel endpoint.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">County name</label>
                  <Input
                    value={addCountyForm.name}
                    onChange={(e) => setAddCountyForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. St. Charles"
                    data-testid="add-county-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">State (2 letters)</label>
                    <Input
                      value={addCountyForm.state}
                      onChange={(e) => setAddCountyForm((f) => ({ ...f, state: e.target.value.toUpperCase().slice(0, 2) }))}
                      placeholder="MO"
                      maxLength={2}
                      data-testid="add-county-state-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">City <span className="text-gray-400 font-normal">(optional)</span></label>
                    <Input
                      value={addCountyForm.city}
                      onChange={(e) => setAddCountyForm((f) => ({ ...f, city: e.target.value }))}
                      placeholder="St. Peters"
                      data-testid="add-county-city-input"
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <Button variant="outline" size="sm" onClick={() => setAddCountyOpen(false)} data-testid="add-county-cancel-btn">Cancel</Button>
                <Button
                  size="sm"
                  onClick={handleAddCounty}
                  disabled={addCountySaving || !addCountyForm.name.trim() || addCountyForm.state.trim().length !== 2}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="add-county-submit-btn"
                >
                  {addCountySaving ? (<><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Adding…</>) : (<><Plus className="w-4 h-4 mr-1" /> Add county</>)}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ArcGIS auto-discovery for the just-added county */}
        {discoverFor && (
          <AutoDiscoverArcGISDialog
            countyId={discoverFor.county_id}
            countyName={discoverFor.name}
            onPromoted={() => { discoverPromotedRef.current = true; }}
            onClose={() => {
              setDiscoverFor(null);
              loadFetcherCounties();
              if (discoverPromotedRef.current) {
                setMessage({ type: 'success', text: `${discoverFor.name} is wired up — pick it in the dropdown and fetch parcels.` });
              } else {
                setMessage({
                  type: 'info',
                  text: `${discoverFor.name} stays manual-only for now — you can still use Upload & Split below, or retry discovery later from its Parcel Data Mgmt county page.`,
                });
              }
              discoverPromotedRef.current = false;
            }}
          />
        )}
      </div>

      {/* Convert panel */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-5" data-testid="parcel-converter-upload-panel">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-600" /> Upload &amp; Split
            </h3>
            <p className="text-xs text-gray-600 mt-1">
              Output CSVs contain only <code className="bg-blue-100 px-1 rounded text-[10px]">latitude, longitude, full_address</code>. Rows missing lat/lng are skipped. Files larger than {SMALL_THRESHOLD_MB} MB are uploaded in 4 MB chunks automatically.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <div className="sm:col-span-1">
            <label className="text-xs font-medium text-gray-700 flex items-center gap-1 mb-1">
              <Settings className="w-3 h-3" /> Rows per output CSV
            </label>
            <Input
              type="number"
              min={100}
              max={1_000_000}
              step={1000}
              value={rowsPerCsv}
              onChange={(e) => setRowsPerCsv(Math.max(100, Math.min(1_000_000, Number(e.target.value) || 0)))}
              disabled={working}
              data-testid="parcel-converter-rows-input"
            />
            <p className="text-[10px] text-gray-500 mt-0.5">Between 100 and 1,000,000. Default: 50,000.</p>
          </div>
          <div className="sm:col-span-2 flex items-end">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_EXTS}
              onChange={handleFileSelected}
              className="hidden"
              data-testid="parcel-converter-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={working}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="parcel-converter-upload-btn"
            >
              {working ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {working ? 'Working...' : 'Choose File & Convert'}
            </Button>
          </div>
        </div>

        {/* live progress */}
        {progress && (
          <div className="mt-3 p-3 bg-white border-2 border-blue-300 rounded-lg" data-testid="parcel-converter-progress">
            <div className="flex items-center justify-between mb-1 text-xs">
              <span className="font-semibold text-gray-700">Upload</span>
              <span className="font-mono text-gray-600">{progress.upload_progress || 0}%</span>
            </div>
            <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
              <div className={`h-full transition-all ${progress.upload_progress >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${progress.upload_progress || 0}%` }} />
            </div>
            {(progress.phase === 'converting' || progress.phase === 'completed' || progress.phase === 'failed') && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1 text-xs">
                  <span className="font-semibold text-gray-700">Conversion</span>
                  <span className="font-mono text-gray-600">
                    {progress.rows_emitted != null
                      ? `${progress.rows_emitted.toLocaleString()} rows written across ${progress.output_files?.length || 0} files`
                      : 'starting...'}
                  </span>
                </div>
                <div className="relative w-full h-2 bg-indigo-100 rounded-full overflow-hidden">
                  {progress.phase === 'completed' ? (
                    <div className="h-full bg-emerald-500 w-full" />
                  ) : progress.phase === 'failed' ? (
                    <div className="h-full bg-rose-500 w-full" />
                  ) : (
                    <div className="absolute inset-y-0 left-0 w-1/3 bg-indigo-500 animate-pulse" />
                  )}
                </div>
                {progress.rows_scanned > 0 && (
                  <p className="text-[10px] text-gray-500 mt-0.5">
                    Scanned {progress.rows_scanned.toLocaleString()} · Emitted {(progress.rows_emitted || 0).toLocaleString()} · Skipped {(progress.rows_skipped || 0).toLocaleString()}
                  </p>
                )}
              </div>
            )}
            <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-gray-100">
              <p className="text-sm font-medium text-gray-800 truncate flex-1">{progress.message}</p>
              <Badge className={`${progress.phase === 'completed' ? 'bg-emerald-100 text-emerald-800' : progress.phase === 'failed' ? 'bg-rose-100 text-rose-800' : 'bg-blue-100 text-blue-800'} border-0 shrink-0`}>
                {progress.phase}
              </Badge>
            </div>
          </div>
        )}
      </div>

      {/* Status banner */}
      {message && (
        <div className={`rounded-lg p-3 text-sm flex items-start gap-2 ${
          message.type === 'success' ? 'bg-emerald-50 text-emerald-900 border border-emerald-200' :
          message.type === 'error' ? 'bg-rose-50 text-rose-900 border border-rose-200' :
          'bg-blue-50 text-blue-900 border border-blue-200'
        }`} data-testid="parcel-converter-message">
          {message.type === 'success' ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Parcel-Data Audit Reports — populated by clicking "Audit" on a
          county detail page. Lists every audited county with its dupe
          status and a one-click Wipe & Re-fetch button for the bad ones. */}
      <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="parcel-converter-audit-reports">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-violet-600" />
            <h3 className="font-bold text-gray-900">Parcel Data Audit Reports</h3>
            {auditReports.length > 0 && (
              <Badge className="bg-violet-100 text-violet-800 border border-violet-200">
                {auditReports.length}
              </Badge>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={loadAuditReports} disabled={loadingAudits} data-testid="audit-reports-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loadingAudits ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        {loadingAudits && !auditReports.length ? (
          <div className="text-center py-6 text-gray-400">
            <RefreshCw className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : auditReports.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm" data-testid="audit-reports-empty">
            No audit reports yet. Open any county in <strong>Parcel Data Mgmt</strong> and click
            <code className="bg-gray-100 px-1.5 py-0.5 rounded mx-1">Audit</code>
            to scan for duplicates.
          </div>
        ) : (
          <div className="space-y-2">
            {auditReports.map((r) => {
              const hasDupes = (r.duplicate_groups || 0) > 0;
              const isRebuilding = rebuildingCounty === r.county_id;
              return (
                <div
                  key={r.county_id}
                  className={`border-2 rounded-lg p-3 transition ${
                    hasDupes ? 'border-red-200 bg-red-50/40 hover:border-red-300' : 'border-emerald-200 bg-emerald-50/40 hover:border-emerald-300'
                  }`}
                  data-testid={`audit-row-${r.county_id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{r.county_name}</span>
                        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-[11px] text-gray-600">{r.county_id}</code>
                        {hasDupes ? (
                          <Badge className="bg-red-100 text-red-800 border border-red-300 text-[10px]">
                            {(r.duplicate_groups || 0).toLocaleString()} dupe groups · {(r.excess_parcels || 0).toLocaleString()} excess
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px]">
                            <CheckCircle2 className="w-3 h-3 mr-1 inline" /> Clean
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {(r.total_parcels || 0).toLocaleString()} parcels
                        {r.coverage_pct !== null && r.coverage_pct !== undefined && (
                          <> · {r.coverage_pct}% of expected {(r.expected_total || 0).toLocaleString()}</>
                        )}
                        {' · '}
                        Audited {r.audited_at ? new Date(r.audited_at).toLocaleString() : 'never'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/parcel-data-v2/county/${r.county_id}`)}
                        data-testid={`audit-row-detail-${r.county_id}`}
                      >
                        Open county
                      </Button>
                      {hasDupes && (
                        <Button
                          size="sm"
                          onClick={() => rebuildCounty(r.county_id, r.county_name)}
                          disabled={isRebuilding || !!rebuildingCounty}
                          className="bg-red-600 hover:bg-red-700 text-white"
                          data-testid={`audit-row-rebuild-${r.county_id}`}
                        >
                          {isRebuilding
                            ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Starting…</>
                            : <><Trash2 className="w-3.5 h-3.5 mr-1" /> Wipe & Re-fetch</>}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Jobs list */}
      <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="parcel-converter-jobs">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900">Conversion Jobs</h3>
          <Button variant="outline" size="sm" onClick={loadJobs} disabled={loadingJobs} data-testid="parcel-converter-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loadingJobs ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        {loadingJobs && !jobs.length ? (
          <div className="text-center py-8 text-gray-400">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-8 text-gray-400" data-testid="parcel-converter-no-jobs">
            No conversion jobs yet. Upload a file above to get started.
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => {
              const expanded = isJobExpanded(job);
              const allParts = job.output_files || [];
              const finalizedCount = allParts.filter((f) => f.finalized && !f.partial).length;
              return (
              <div key={job.job_id} className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition" data-testid={`parcel-converter-job-${job.job_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <FileText className="w-4 h-4 text-gray-500 shrink-0" />
                      <span className="font-semibold text-gray-900 truncate">{job.filename}</span>
                      <Badge className={`${job.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : job.status === 'failed' ? 'bg-rose-100 text-rose-800' : job.status === 'converting' ? 'bg-indigo-100 text-indigo-800' : 'bg-blue-100 text-blue-800'} border-0 text-[10px]`}>
                        {job.status}
                      </Badge>
                      {job.is_stuck && (
                        <Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] animate-pulse" data-testid={`stuck-badge-${job.job_id}`}>
                          <AlertTriangle className="w-2.5 h-2.5 mr-0.5 inline" /> Stuck — no heartbeat
                        </Badge>
                      )}
                      {!expanded && allParts.length > 0 && (
                        <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]" data-testid={`collapsed-parts-count-${job.job_id}`}>
                          {finalizedCount}/{allParts.length} parts · hidden
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      <code className="bg-gray-100 px-1 rounded text-[10px]">{job.job_id}</code>
                      {' · '}{(job.expected_bytes || 0) > 0 ? fmtBytes(job.expected_bytes) : '—'}
                      {' · '}{job.rows_per_csv?.toLocaleString()} rows/file
                      {' · '}{(job.rows_emitted || 0).toLocaleString()} rows emitted
                      {' · '}{(job.output_files || []).length} parts
                      {job.started_at && <> · {new Date(job.started_at).toLocaleString()}</>}
                    </p>
                    {job.status !== 'completed' && job.message && (
                      <p className="text-xs text-gray-600 mt-1">{job.message}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                    {/* Resume button — only for failed Parcel Fetcher jobs
                        that have a saved checkpoint. Continues paginating
                        ArcGIS from the exact offset where it stopped. */}
                    {job.source_type === 'fetch'
                      && job.status === 'failed'
                      && job.resume_offset != null
                      && job.resume_offset > 0 && (
                      <Button
                        size="sm"
                        onClick={() => handleResumeFetch(job)}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid={`resume-fetch-btn-${job.job_id}`}
                        title={`Continue from offset ${Number(job.resume_offset).toLocaleString()} — already saved ${Number(job.rows_emitted || 0).toLocaleString()} rows`}
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />
                        Resume from {Number(job.resume_offset).toLocaleString()}
                      </Button>
                    )}
                    {(() => {
                      const finalizedFiles = (job.output_files || []).filter((f) => f.finalized && !f.partial);
                      const hasFinalized = finalizedFiles.length > 0;
                      const stillRunning = job.status === 'uploading' || job.status === 'converting' || job.status === 'finalizing';
                      if (!hasFinalized) return null;
                      return (
                        <>
                          <Button
                            size="sm"
                            onClick={() => openPushModal(job)}
                            disabled={job.push_status === 'running'}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid={`push-to-county-btn-${job.job_id}`}
                            title={stillRunning
                              ? `Push the ${finalizedFiles.length} finalized chunk(s) now (more will follow once conversion finishes)`
                              : `Push ${finalizedFiles.length} chunk(s) to a county`}
                          >
                            <Database className="w-3.5 h-3.5 mr-1" />
                            {job.push_status === 'running'
                              ? `Pushing… ${Math.round(job.push_progress || 0)}%`
                              : stillRunning
                                ? `Push ${finalizedFiles.length} so far`
                                : 'Push to Parcel Data Mgmt'}
                          </Button>
                          {job.status === 'completed' && (
                            <a
                              href={zipUrl(job.job_id)}
                              className="inline-flex items-center gap-1 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded transition"
                              data-testid={`download-zip-${job.job_id}`}
                            >
                              <Package className="w-3 h-3" /> Download All as .zip
                            </a>
                          )}
                        </>
                      );
                    })()}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleJobCollapse(job.job_id, expanded)}
                      className="text-gray-500 hover:bg-gray-100"
                      data-testid={`collapse-job-btn-${job.job_id}`}
                      title={expanded ? 'Hide details (parts list, push status)' : 'Show details (parts list, push status)'}
                      aria-expanded={expanded}
                    >
                      {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteJob(job.job_id)}
                      className="text-rose-600 hover:bg-rose-50"
                      data-testid={`delete-job-${job.job_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Stuck-recovery banner — shown when the heartbeat detector
                    flags this job as dead. Offers a one-click "Mark failed
                    & retry" that wipes any stale part files and re-runs the
                    parser on the existing source.bin (no re-upload needed). */}
                {expanded && job.is_stuck && (
                  <div className="mt-2 p-2.5 rounded border border-amber-300 bg-amber-50 text-amber-900" data-testid={`stuck-banner-${job.job_id}`}>
                    <p className="text-xs font-semibold flex items-center gap-1.5 mb-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      This job hasn't sent a heartbeat in 2+ minutes — the background worker probably died.
                    </p>
                    <p className="text-[11px] text-amber-800 mb-2">
                      Recovery reuses the original uploaded file on disk; no re-upload needed.
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        onClick={() => handleStuckRecover(job.job_id, true)}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid={`stuck-recover-retry-${job.job_id}`}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" /> Clean up & retry
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStuckRecover(job.job_id, false)}
                        className="border-amber-300 text-amber-800"
                        data-testid={`stuck-mark-failed-${job.job_id}`}
                      >
                        Mark as failed
                      </Button>
                    </div>
                  </div>
                )}

                {/* Push status panel */}
                {expanded && job.push_status && (
                  <div className={`mt-2 p-2 rounded border text-xs ${
                    job.push_status === 'completed' ? 'bg-emerald-50 border-emerald-200' :
                    job.push_status === 'failed' ? 'bg-rose-50 border-rose-200' :
                    'bg-blue-50 border-blue-200'
                  }`} data-testid={`push-progress-${job.job_id}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-semibold">
                        Push to <code className="bg-white px-1 rounded">parcels_v2_{job.push_target_county}</code>
                      </span>
                      <Badge className={`${
                        job.push_status === 'completed' ? 'bg-emerald-200 text-emerald-900' :
                        job.push_status === 'failed' ? 'bg-rose-200 text-rose-900' :
                        'bg-blue-200 text-blue-900'
                      } border-0 text-[10px]`}>{job.push_status}</Badge>
                    </div>
                    {job.push_total_files > 0 && (
                      <div className="flex items-center gap-2 mb-1">
                        <div className="flex-1 h-1.5 bg-white/60 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              job.push_status === 'completed' ? 'bg-emerald-500' :
                              job.push_status === 'failed' ? 'bg-rose-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${job.push_progress || 0}%` }}
                          />
                        </div>
                        <span className="text-[10px] font-bold tabular-nums text-gray-700" data-testid={`push-progress-pct-${job.job_id}`}>
                          {Math.round(job.push_progress || 0)}%
                        </span>
                      </div>
                    )}
                    <p className="text-gray-700">{job.push_message}</p>
                    {(job.push_imported != null || job.push_skipped != null) && (
                      <p className="text-gray-500 mt-0.5">
                        {(job.push_files_processed || 0)}/{(job.push_total_files || 0)} files ·{' '}
                        {(job.push_imported || 0).toLocaleString()} imported ·{' '}
                        {(job.push_skipped || 0).toLocaleString()} skipped
                        {job.push_total_rows > 0 && job.push_status === 'running' && (
                          <> · {(job.push_rows_done || 0).toLocaleString()} of ~{job.push_total_rows.toLocaleString()} rows</>
                        )}
                      </p>
                    )}
                  </div>
                )}
                {expanded && (() => {
                  if (allParts.length === 0) return null;
                  const rowsPerCsv = job.rows_per_csv || 25000;
                  return (
                    <div className="mt-2.5" data-testid={`parcel-converter-parts-${job.job_id}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-gray-700">
                          Parts · {finalizedCount} of {allParts.length} ready
                          {job.status === 'converting' && ' (current part still being written)'}
                        </span>
                        {job.status === 'completed' && (
                          <span className="text-[10px] text-emerald-700 font-semibold">All parts ready</span>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        {allParts.map((f, idx) => {
                          const isFinalized = !!f.finalized && !f.partial;
                          const isCurrent = !isFinalized && idx === allParts.length - 1 && job.status === 'converting';
                          // For the in-progress part the % is rows / rows_per_csv
                          const pct = isCurrent
                            ? Math.min(100, Math.round(((f.rows || 0) / rowsPerCsv) * 100))
                            : (isFinalized ? 100 : 0);
                          return (
                            <div
                              key={f.name}
                              className={`flex items-center gap-3 px-2.5 py-1.5 rounded border ${
                                isFinalized
                                  ? 'bg-emerald-50 border-emerald-200'
                                  : isCurrent
                                    ? 'bg-blue-50 border-blue-200'
                                    : 'bg-gray-50 border-gray-200'
                              }`}
                              data-testid={`part-row-${job.job_id}-${f.name}`}
                            >
                              <FileText className={`w-3.5 h-3.5 shrink-0 ${
                                isFinalized ? 'text-emerald-700' : isCurrent ? 'text-blue-700' : 'text-gray-500'
                              }`} />
                              <code className="text-[11px] font-semibold text-gray-800 shrink-0">{f.name}</code>
                              {/* Progress bar — fills 100% when finalized, animated while in progress */}
                              <div className="flex-1 min-w-[80px] h-1.5 bg-white/70 rounded-full overflow-hidden border border-gray-200">
                                <div
                                  className={`h-full transition-all ${
                                    isFinalized ? 'bg-emerald-500'
                                    : isCurrent ? 'bg-blue-500 animate-pulse'
                                    : 'bg-gray-400'
                                  }`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-[10px] text-gray-600 shrink-0 tabular-nums w-20 text-right">
                                {(f.rows || 0).toLocaleString()} / {rowsPerCsv.toLocaleString()}
                              </span>
                              <span className="text-[10px] text-gray-500 shrink-0 tabular-nums w-16 text-right">
                                {fmtBytes(f.size || 0)}
                              </span>
                              {isFinalized ? (
                                <a
                                  href={fileUrl(job.job_id, f.name)}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded transition shrink-0"
                                  data-testid={`download-file-${job.job_id}-${f.name}`}
                                  title="Download this part as a CSV"
                                >
                                  <Download className="w-3 h-3" /> Download
                                </a>
                              ) : isCurrent ? (
                                <span className="px-2 py-0.5 text-[10px] font-semibold bg-blue-200 text-blue-900 rounded shrink-0">
                                  Writing... {pct}%
                                </span>
                              ) : (
                                <span className="px-2 py-0.5 text-[10px] font-semibold bg-gray-200 text-gray-700 rounded shrink-0">
                                  Pending
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Push to Parcel Data Mgmt modal */}
      {pushModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closePushModal} data-testid="push-modal-overlay">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="push-modal">
            <div className="px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="font-bold text-gray-900">Push converted CSVs to Parcel Data Mgmt</h3>
              <p className="text-xs text-gray-500 mt-0.5">Source: <code className="bg-white px-1 rounded text-[10px]">{pushModal.filename}</code></p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Target county</label>
                <select
                  value={selectedCountyId}
                  onChange={(e) => setSelectedCountyId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="push-county-select"
                >
                  <option value="">— Select a county —</option>
                  {v2Counties.map((c) => (
                    <option key={c.county_id} value={c.county_id}>
                      {c.name} ({c.state})
                    </option>
                  ))}
                </select>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                <strong>What happens:</strong> Every <code className="bg-white px-1 rounded text-[10px]">part_NNNN.csv</code> file produced by this conversion job will be upserted directly into <code className="bg-white px-1 rounded text-[10px]">parcels_v2_{selectedCountyId || '<county>'}</code>. SHA-1 dedup means re-pushing the same job is a no-op (already-imported rows are skipped). The county's <code className="bg-white px-1 rounded text-[10px]">last_upload</code> metadata is updated to point at this conversion job.
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" onClick={closePushModal} disabled={pushing} data-testid="push-cancel-btn">Cancel</Button>
              <Button
                onClick={handleStartPush}
                disabled={!selectedCountyId || pushing}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="push-confirm-btn"
              >
                {pushing ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Database className="w-4 h-4 mr-1" />}
                Push to {selectedCountyId || 'county'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Endpoint validation modal — surfaces dead URLs, DNS failures,
          missing services, and large drift between actual vs estimated
          parcel counts. Shows results grouped by health bucket so
          admins can spot bad endpoints before sinking 30 min into a
          doomed fetch. */}
      {validateModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setValidateModalOpen(false)}
          data-testid="validate-modal-overlay"
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="validate-modal"
          >
            <div className="px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" /> County Endpoint Health Check
                </h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  Pings every configured ArcGIS endpoint and compares the reported count to your estimate. Counties flagged with HTTP errors are dead and need a new URL in <code className="bg-white px-1 rounded">usa_parcel_config.py</code>.
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setValidateModalOpen(false)}
                data-testid="validate-modal-close-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-auto px-5 py-4">
              {validating && (
                <div className="text-center py-12 text-gray-500" data-testid="validate-loading">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-emerald-600" />
                  <p className="text-sm">Pinging all configured county endpoints in parallel…</p>
                  <p className="text-xs text-gray-400 mt-1">Usually finishes in 5–15 seconds.</p>
                </div>
              )}

              {!validating && validateReport?.error && (
                <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm text-rose-900" data-testid="validate-error">
                  <AlertCircle className="w-4 h-4 inline mr-1" /> Validation failed: {validateReport.error}
                </div>
              )}

              {!validating && validateReport?.summary && (
                <>
                  {/* Summary chips */}
                  <div className="flex flex-wrap gap-2 mb-3" data-testid="validate-summary">
                    <Badge className="bg-emerald-100 text-emerald-900 border-0">
                      OK · {validateReport.summary.ok}
                    </Badge>
                    {validateReport.summary.count_off > 0 && (
                      <Badge className="bg-amber-100 text-amber-900 border-0">
                        Drift &gt;{validateReport.summary.drift_threshold_pct}% · {validateReport.summary.count_off}
                      </Badge>
                    )}
                    {validateReport.summary.http_error > 0 && (
                      <Badge className="bg-rose-100 text-rose-900 border-0">
                        HTTP error · {validateReport.summary.http_error}
                      </Badge>
                    )}
                    {validateReport.summary.network_error > 0 && (
                      <Badge className="bg-rose-100 text-rose-900 border-0">
                        Network error · {validateReport.summary.network_error}
                      </Badge>
                    )}
                    {validateReport.summary.no_count > 0 && (
                      <Badge className="bg-rose-100 text-rose-900 border-0">
                        Service missing · {validateReport.summary.no_count}
                      </Badge>
                    )}
                    <Badge className="bg-gray-100 text-gray-700 border-0">
                      Manual · {validateReport.summary.manual}
                    </Badge>
                    <Badge className="bg-gray-100 text-gray-700 border-0 ml-auto">
                      {new Date(validateReport.summary.checked_at).toLocaleTimeString()}
                    </Badge>
                  </div>

                  {/* Results table */}
                  <div className="overflow-x-auto border border-gray-200 rounded-lg" data-testid="validate-results-table">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-700">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold">Health</th>
                          <th className="text-left px-3 py-2 font-semibold">State</th>
                          <th className="text-left px-3 py-2 font-semibold">County</th>
                          <th className="text-right px-3 py-2 font-semibold">Actual</th>
                          <th className="text-right px-3 py-2 font-semibold">Estimated</th>
                          <th className="text-right px-3 py-2 font-semibold">Δ%</th>
                          <th className="text-right px-3 py-2 font-semibold">HTTP</th>
                          <th className="text-right px-3 py-2 font-semibold">ms</th>
                        </tr>
                      </thead>
                      <tbody>
                        {validateReport.counties.map((c) => {
                          const rowClass = {
                            ok: 'bg-white',
                            count_off: 'bg-amber-50',
                            http_error: 'bg-rose-50',
                            network_error: 'bg-rose-50',
                            no_count: 'bg-rose-50',
                            manual: 'bg-gray-50 text-gray-500',
                          }[c.health] || 'bg-white';
                          const badgeClass = {
                            ok: 'bg-emerald-200 text-emerald-900',
                            count_off: 'bg-amber-200 text-amber-900',
                            http_error: 'bg-rose-200 text-rose-900',
                            network_error: 'bg-rose-200 text-rose-900',
                            no_count: 'bg-rose-200 text-rose-900',
                            manual: 'bg-gray-200 text-gray-700',
                          }[c.health] || 'bg-gray-200 text-gray-700';
                          const badgeLabel = {
                            ok: 'OK',
                            count_off: 'Drift',
                            http_error: 'HTTP err',
                            network_error: 'Net err',
                            no_count: 'No svc',
                            manual: 'Manual',
                          }[c.health] || c.health;
                          return (
                            <React.Fragment key={c.county_id}>
                              <tr className={`${rowClass} border-t border-gray-200`} data-testid={`validate-row-${c.county_id}`}>
                                <td className="px-3 py-2">
                                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${badgeClass}`}>
                                    {badgeLabel}
                                  </span>
                                </td>
                                <td className="px-3 py-2 font-mono text-[10px]">{c.state || '—'}</td>
                                <td className="px-3 py-2 font-medium text-gray-900">{c.name}</td>
                                <td className="px-3 py-2 text-right font-mono tabular-nums">
                                  {c.actual_count != null ? c.actual_count.toLocaleString() : '—'}
                                </td>
                                <td className="px-3 py-2 text-right font-mono tabular-nums text-gray-500">
                                  {c.estimated_parcels ? c.estimated_parcels.toLocaleString() : '—'}
                                </td>
                                <td className={`px-3 py-2 text-right font-mono tabular-nums ${
                                  c.delta_pct == null ? 'text-gray-400'
                                  : Math.abs(c.delta_pct) > validateReport.summary.drift_threshold_pct ? 'text-amber-700 font-semibold'
                                  : 'text-gray-700'
                                }`}>
                                  {c.delta_pct != null ? `${c.delta_pct > 0 ? '+' : ''}${c.delta_pct}%` : '—'}
                                </td>
                                <td className="px-3 py-2 text-right font-mono">{c.http_code ?? '—'}</td>
                                <td className="px-3 py-2 text-right font-mono text-gray-500">{c.response_ms ?? '—'}</td>
                              </tr>
                              {c.error && (
                                <tr className={rowClass} data-testid={`validate-row-error-${c.county_id}`}>
                                  <td colSpan={8} className="px-3 pb-2 text-[10px] text-rose-800">
                                    └─ <span className="font-mono">{c.error}</span>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-[11px] text-gray-500 mt-3">
                    Drift threshold: <strong>{validateReport.summary.drift_threshold_pct}%</strong> deviation from the configured <code className="bg-gray-100 px-1 rounded">estimated_parcels</code>.
                    Counties flagged as HTTP/network/no-svc errors need a new URL in <code className="bg-gray-100 px-1 rounded">backend/services/usa_parcel_config.py</code> — same root cause as the Dallas + Denver fixes.
                  </p>
                </>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={handleValidateEndpoints}
                disabled={validating}
                data-testid="validate-recheck-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${validating ? 'animate-spin' : ''}`} />
                Re-check now
              </Button>
              <Button
                onClick={() => setValidateModalOpen(false)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="validate-modal-done-btn"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminParcelConverter;
